import { CiroButtonStyleEnum } from "../shared/CiroButton";
import classNames from "classnames";
import { SparklesIcon } from "@heroicons/react/20/solid";
import CiroButton from "../shared/CiroButton";
import { ApolloError, gql } from "@apollo/client";
import {
  AutopilotPreview_AutopilotSessionPreviewFragment,
  AutopilotPreviewTable_ContactMatchPreviewFragmentDoc,
  AutopilotPreview_AutopilotSessionFragment,
  AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponseFragmentDoc,
} from "../../__generated__/graphql";
import { useCallback, useContext, useState } from "react";
import { useFragment } from "../../__generated__";
import AutopilotPreviewRunAutopilotModal from "./AutopilotPreviewRunAutopilotModal";
import AutopilotPreviewTable, {
  AutopilotPreviewTable_ContactMatchPreview,
} from "./AutopilotPreviewTable";
import CiroAlert from "../shared/CiroAlert";
import AutopilotCuratedSearchContext from "../../contexts/AutopilotCuratedSearchContext";
import InternalAutopilotHydrationContext, {
  PreviewContactFeedbackMap,
} from "../../contexts/InternalAutopilotHydrationContext";
import AutopilotSessionContinueSearchModal from "../automation/autopilotSession/AutopilotSessionContinueSearchModal";
import { useNavigate } from "react-router-dom";
import AutopilotPreviewSubheaderActions, {
  AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse,
} from "./AutopilotPreviewSubheaderActions";

export const AutopilotPreview_AutopilotSessionPreview = gql`
  fragment AutopilotPreview_AutopilotSessionPreview on AutopilotSessionPreviewResponse {
    ...AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse
    totalContacts
    multisearchDetail {
      contactCountFromPreviewCompanies
      numPreviewCompanies
      numTotalCompanies
    }
    contacts {
      org_contact {
        id
      }
    }
    ...AutopilotPreviewTable_ContactMatchPreview
  }
  ${AutopilotPreviewTable_ContactMatchPreview}
  ${AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse}
`;

export const AutopilotPreview_AutopilotSession = gql`
  fragment AutopilotPreview_AutopilotSession on AutopilotSession {
    id
    name
    number_contacts_scanned
    curatedSearch {
      internalCuratedSearch {
        total_profiles
      }
    }
  }
`;

export const enum AutopilotPreviewLoadingState {
  FetchingContacts = "fetchingContacts",
  RotatingContacts = "rotatingContacts",
  Error = "error",
  Success = "success",
  CompletingProgress = "completingProgress",
  ShowResults = "showResults",
}

interface IAutopilotPreview {
  autopilotSessionPreview: AutopilotPreview_AutopilotSessionPreviewFragment;
  autopilotSessionPreviewError?: ApolloError | null;
  curatedSearchId: number | null;
  isRemixStep?: boolean;
  previewContactFeedback: PreviewContactFeedbackMap;
  setPreviewContactFeedback: (feedback: PreviewContactFeedbackMap) => void;
  existingAutopilotSession: AutopilotPreview_AutopilotSessionFragment | null;
  setMinimized: (minimized: boolean) => void;
}

const AutopilotPreview = ({
  autopilotSessionPreview,
  autopilotSessionPreviewError,
  curatedSearchId,
  isRemixStep,
  previewContactFeedback,
  setPreviewContactFeedback,
  existingAutopilotSession,
  setMinimized,
}: IAutopilotPreview) => {
  const navigate = useNavigate();
  const [isRunAutopilotModalOpen, setIsRunAutopilotModalOpen] = useState(false);
  const {
    setShowRemixStep,
    resetRemix,
    setStartedRemixingFilters,
    setRemixPrompt,
  } = useContext(AutopilotCuratedSearchContext);
  const { resetHydrationValues } = useContext(
    InternalAutopilotHydrationContext,
  );
  const contactsMatchPreviewTable = useFragment(
    AutopilotPreviewTable_ContactMatchPreviewFragmentDoc,
    autopilotSessionPreview,
  );

  const hasNegativeFeedback = [...previewContactFeedback.values()].some(
    (value) => value.isBadFit,
  );
  const [isContinueSearchModalOpen, setIsContinueSearchModalOpen] =
    useState(false);

  const handleRemixClick = () => {
    resetRemix!();
    setMinimized(true);

    if (!isRemixStep) {
      setShowRemixStep!(true);
    }
    if (previewContactFeedback.size > 0) {
      setRemixPrompt!(
        "I want to improve the results based on the feedback I provided on the previous contacts.",
      );
      setStartedRemixingFilters!(true);
    }
  };

  const onStartedAutopilot = useCallback(() => {
    resetHydrationValues({});
  }, [resetHydrationValues]);

  const subheaderActionsSessionPreview = useFragment(
    AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponseFragmentDoc,
    autopilotSessionPreview,
  );

  if (!curatedSearchId) {
    return null;
  }

  return (
    <div className={classNames("w-full")}>
      {autopilotSessionPreviewError && (
        <CiroAlert
          message={
            "An error occurred while loading your search preview. Try hitting refresh or contacting support at support@ciro.io."
          }
          level="error"
        />
      )}

      <div
        className={classNames(
          "transition-opacity",
          "duration-500",
          "ease-in-out",
        )}
      >
        <div className={classNames("w-full", "py-4")}>
          <div className={classNames("h-5", "flex", "items-center", "gap-2")}>
            <div>
              <SparklesIcon className={classNames("text-orange-500", "h-6")} />
            </div>
            <div className={classNames("text-md", "font-semibold")}>
              Preview results
            </div>
            <AutopilotPreviewSubheaderActions
              autopilotSessionPreview={subheaderActionsSessionPreview}
              curatedSearchId={curatedSearchId}
            />
          </div>
          {autopilotSessionPreview && (
            <div className={classNames("text-sm", "mt-2", "text-zinc-500")}>
              {autopilotSessionPreview.contacts.length > 0
                ? `Review the first ${autopilotSessionPreview.contacts.length} contacts based on your criteria.`
                : "No contacts found matching your search criteria."}
            </div>
          )}
        </div>
        <div>
          <div className={classNames("max-h-[60vh]", "overflow-auto")}>
            {autopilotSessionPreview &&
            autopilotSessionPreview.contacts.length === 0 ? (
              <div className={classNames("p-6", "text-center")}>
                <div className={classNames("text-lg", "font-medium", "mb-2")}>
                  No contacts found
                </div>
                <div className={classNames("text-zinc-500", "mb-2", "text-sm")}>
                  We couldn't find any contacts matching your search criteria.
                  Try adjusting your search parameters to broaden your search.
                </div>
              </div>
            ) : (
              <AutopilotPreviewTable
                contactsMatchPreview={contactsMatchPreviewTable}
                previewContactFeedback={previewContactFeedback}
                setPreviewContactFeedback={setPreviewContactFeedback}
              />
            )}
          </div>
          <div className={classNames("-mx-4", "pt-4")}>
            <div
              className={classNames(
                "w-full",
                "flex",
                "justify-end",
                "gap-2",
                "pt-4",
                "border-t",
                "border-zinc-200",
                "px-4",
              )}
            >
              <CiroButton
                analyticsField="Remix autopilot search"
                style={
                  hasNegativeFeedback
                    ? CiroButtonStyleEnum.LOUD
                    : CiroButtonStyleEnum.SECONDARY
                }
                onClick={handleRemixClick}
              >
                <div className={classNames("flex", "items-center", "gap-1")}>
                  <span>Improve Results</span>
                </div>
              </CiroButton>
              <CiroButton
                disabled={
                  (autopilotSessionPreview?.contacts.length ?? 0) === 0 ||
                  Boolean(autopilotSessionPreviewError)
                }
                analyticsField="Start autopilot internal search"
                style={
                  hasNegativeFeedback
                    ? CiroButtonStyleEnum.SECONDARY
                    : CiroButtonStyleEnum.LOUD
                }
                onClick={() =>
                  existingAutopilotSession
                    ? setIsContinueSearchModalOpen(true)
                    : setIsRunAutopilotModalOpen(true)
                }
              >
                <div className={classNames("flex", "items-center", "gap-1")}>
                  <span>Search for more contacts</span>
                </div>
              </CiroButton>
            </div>
          </div>
        </div>
      </div>
      <AutopilotPreviewRunAutopilotModal
        isOpen={isRunAutopilotModalOpen}
        onClose={() => setIsRunAutopilotModalOpen(false)}
        curatedSearchId={curatedSearchId}
        totalContacts={autopilotSessionPreview?.totalContacts || 0}
        isMultisearch={autopilotSessionPreview?.multisearchDetail !== null}
        onStartedAutopilot={onStartedAutopilot}
      />
      {existingAutopilotSession && (
        <AutopilotSessionContinueSearchModal
          isOpen={isContinueSearchModalOpen}
          onClose={() => setIsContinueSearchModalOpen(false)}
          autopilotSessionId={existingAutopilotSession.id}
          onRestartedSession={() => {
            navigate(`/autopilot/session/${existingAutopilotSession.id}`);
            resetHydrationValues({});
          }}
          runningState={null}
          contactsScanned={existingAutopilotSession.number_contacts_scanned}
          totalProfiles={
            existingAutopilotSession.curatedSearch?.internalCuratedSearch
              ?.total_profiles
          }
          customName={`Existing search found. Continue searching ${existingAutopilotSession.name}`}
          initialSkipContactsYouveAlreadySeen={false}
        />
      )}
    </div>
  );
};

export default AutopilotPreview;
